/**
 * useContacts.js — Device contacts integration (3-tier)
 *
 * Priority order:
 * 1. Native (Capacitor) — iOS App Store build, uses @capacitor-community/contacts
 *    Uses `pickContact` (system picker, one at a time) — no bulk-load permission
 *    prompt, fast, native UI, privacy-preserving.
 * 2. Web Contact Picker API — Chrome on Android (and any browser that supports it)
 * 3. Manual entry fallback — Safari on iOS (no contact API), older browsers
 *
 * IMPORTANT: Uses STATIC import for the Capacitor plugin. Dynamic import
 * (`await import(...)`) stalls in WKWebView during early use — this was the
 * same bug fixed in Session 44 for the biometric plugin. The plugin's
 * `registerPlugin()` returns a JS proxy that works in both native and browser
 * contexts, so static import is safe on web.
 */

import { useState, useCallback } from 'react';
import { Contacts } from '@capacitor-community/contacts';

// ── Platform detection ──────────────────────────────────────────────

function isNativePlatform() {
  return !!window.Capacitor?.isNativePlatform?.();
}

/**
 * Check if the browser supports the Web Contact Picker API.
 * Available in Chrome 80+ on Android. NOT available in Safari/iOS.
 */
function hasWebContactPicker() {
  return 'contacts' in navigator && 'ContactsManager' in window;
}

/**
 * Determine the best available contact method.
 * Returns: 'native' | 'web-picker' | 'manual'
 */
function getContactMethod() {
  if (isNativePlatform()) return 'native';
  if (hasWebContactPicker()) return 'web-picker';
  return 'manual';
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Wrap a promise in a timeout so a silently-hanging native call can't
 * freeze the UI forever. Returns { ok: true, value } or { ok: false, reason }.
 */
function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      console.warn(`[Contacts] ${label} timed out after ${ms}ms`);
      resolve({ ok: false, reason: 'timeout' });
    }, ms);
  });
  return Promise.race([
    promise.then(
      (value) => ({ ok: true, value }),
      (err) => ({ ok: false, reason: 'error', err })
    ),
    timeout,
  ]).finally(() => clearTimeout(timer));
}

/**
 * Generate a single-letter avatar from a contact name.
 */
function avatarForName(name) {
  if (!name) return '\u{1F464}';
  return name.charAt(0).toUpperCase();
}

/**
 * Normalize a phone number to E.164-ish for deduplication.
 */
function normalizePhone(raw) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

/**
 * Format a phone number for display: +1XXXXXXXXXX → +1 (XXX) XXX-XXXX
 */
function formatPhone(normalized) {
  if (!normalized || normalized.length < 12) return normalized;
  const digits = normalized.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return normalized;
}

/**
 * Normalize a Capacitor `ContactPayload` into our internal contact shape.
 * Returns null if the contact has neither phone nor email.
 */
function transformCapacitorContact(c) {
  const name = c.name?.display || c.name?.given || 'Unknown';

  const phoneNumbers = c.phones ?? [];
  const emailAddresses = c.emails ?? [];

  if (phoneNumbers.length === 0 && emailAddresses.length === 0) return null;

  // Prefer mobile number if labeled
  let normalized = '';
  if (phoneNumbers.length > 0) {
    const mobilePhone = phoneNumbers.find((p) => {
      const t = (p.type || '').toLowerCase();
      const l = (p.label || '').toLowerCase();
      return t === 'mobile' || l.includes('mobile') || l.includes('cell');
    });
    const primary = mobilePhone || phoneNumbers[0];
    if (primary?.number) normalized = normalizePhone(String(primary.number));
  }

  const primaryEmail = emailAddresses[0]?.address || '';
  const email = typeof primaryEmail === 'string' ? primaryEmail.trim().toLowerCase() : '';

  const dedupeKey = normalized || email;
  if (!dedupeKey) return null;

  const nameStr = typeof name === 'string' ? name : String(name);

  return {
    id: c.contactId || `contact-${dedupeKey}`,
    name: nameStr,
    email,
    phone: normalized ? formatPhone(normalized) : '',
    phoneNormalized: normalized,
    avatar: avatarForName(nameStr),
  };
}

/**
 * Normalize a Web Contact Picker result into our internal contact shape.
 */
function transformWebContact(c) {
  // Web Picker shape: c.name = [string], c.tel = [string], c.email = [string]
  const name = Array.isArray(c.name) ? c.name[0] : c.name || 'Unknown';
  const tels = Array.isArray(c.tel) ? c.tel : [];
  const emails = Array.isArray(c.email) ? c.email : [];

  if (tels.length === 0 && emails.length === 0) return null;

  const normalized = tels[0] ? normalizePhone(tels[0]) : '';
  const email = emails[0] ? emails[0].trim().toLowerCase() : '';

  const dedupeKey = normalized || email;
  if (!dedupeKey) return null;

  return {
    id: `web-${dedupeKey}-${Date.now()}`,
    name,
    email,
    phone: normalized ? formatPhone(normalized) : '',
    phoneNormalized: normalized,
    avatar: avatarForName(name),
  };
}

// ── Native picker (single contact, preferred) ────────────────────────

/**
 * Open the native iOS contact picker (CNContactPickerViewController).
 * User selects ONE contact; we get it back. No NSContacts bulk-permission
 * prompt is triggered — iOS trusts the picker itself with the user's consent.
 *
 * Returns { contact, cancelled, timedOut, error }.
 */
async function pickOneNative() {
  if (!isNativePlatform()) return { contact: null, error: 'not-native' };

  const call = Contacts.pickContact({
    projection: { name: true, phones: true, emails: true, image: false },
  });

  // 30s timeout — picker should show immediately and the user has as long as
  // they need to pick. If it never even opens, something's wrong and we want
  // to fall through rather than hang the app.
  const result = await withTimeout(call, 30_000, 'pickContact');

  if (!result.ok) {
    if (result.reason === 'timeout') {
      return { contact: null, timedOut: true };
    }
    // Plugin throws on user-cancel — treat as cancelled.
    const msg = result.err?.message || '';
    if (msg.toLowerCase().includes('cancel')) {
      return { contact: null, cancelled: true };
    }
    console.warn('[Contacts] pickContact failed:', msg);
    return { contact: null, error: msg };
  }

  const raw = result.value?.contact;
  if (!raw) return { contact: null, cancelled: true };

  const normalized = transformCapacitorContact(raw);
  if (!normalized) {
    // Picked contact has no usable phone or email
    return { contact: null, error: 'no-contact-info' };
  }
  return { contact: normalized };
}

// ── Web Contact Picker loader ───────────────────────────────────────

async function loadWebPickerContacts() {
  try {
    const selected = await navigator.contacts.select(['name', 'tel', 'email'], {
      multiple: true,
    });
    const transformed = (selected || [])
      .map(transformWebContact)
      .filter(Boolean);
    return { contacts: transformed, cancelled: false };
  } catch (err) {
    const cancelErrors = ['InvalidStateError', 'TypeError', 'AbortError'];
    if (cancelErrors.includes(err.name) || err.message?.includes('cancel')) {
      return { contacts: [], cancelled: true };
    }
    console.error('[Contacts] Web picker error:', err);
    return { contacts: [], cancelled: false, error: err };
  }
}

// ── Hook ─────────────────────────────────────────────────────────────

/**
 * useContacts — device contacts integration.
 *
 * Returns:
 *   contacts         — contacts picked so far this session (accumulated across picks)
 *   loading          — true while a pick/load is in progress
 *   permissionDenied — true if iOS returned the contact with no usable info
 *                      (kept for backward compat with existing UI)
 *   method           — 'native' | 'web-picker' | 'manual'
 *   hasContactAccess — !== 'manual'
 *   loaded           — true once at least one pick/load attempt completed
 *   loadContacts()   — trigger picking (returns array of picked contacts)
 *   pickOne()        — native-only: pick one contact via iOS system picker
 */
export function useContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const method = getContactMethod();

  /**
   * Native-only single-contact picker. Shows the iOS system picker, returns
   * the picked contact (or null on cancel/timeout/error). The picked contact
   * is also appended to `contacts` state for any UI that reads from it.
   */
  const pickOne = useCallback(async () => {
    if (method !== 'native') return null;

    setLoading(true);
    try {
      const result = await pickOneNative();
      if (result.contact) {
        setContacts((prev) => {
          const key = result.contact.phoneNormalized || result.contact.email;
          if (prev.some((p) => (p.phoneNormalized || p.email) === key)) return prev;
          return [...prev, result.contact];
        });
        setLoaded(true);
        return result.contact;
      }
      if (result.timedOut) {
        console.warn('[Contacts] Native picker timed out');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [method]);

  /**
   * Unified loader used by the pre-existing picker UI.
   * - native: calls pickOne() and returns [contact] or []
   * - web-picker: opens navigator.contacts.select (multi)
   * - manual: no-op, returns []
   */
  const loadContacts = useCallback(async () => {
    if (method === 'manual') {
      setLoaded(true);
      return [];
    }

    if (method === 'native') {
      const picked = await pickOne();
      return picked ? [picked] : [];
    }

    // Web picker
    setLoading(true);
    try {
      const result = await loadWebPickerContacts();
      if (result.error) {
        setPermissionDenied(true);
        setLoaded(true);
        return [];
      }
      if (result.contacts.length > 0) {
        setContacts((prev) => {
          const existingKeys = new Set(prev.map((p) => p.phoneNormalized || p.email));
          const fresh = result.contacts.filter(
            (c) => !existingKeys.has(c.phoneNormalized || c.email)
          );
          return [...prev, ...fresh];
        });
      }
      setLoaded(true);
      return result.contacts;
    } catch (err) {
      console.error('[Contacts] loadContacts threw:', err);
      setLoaded(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, [method, pickOne]);

  return {
    contacts,
    loading,
    permissionDenied,
    loaded,
    method,
    hasContactAccess: method !== 'manual',
    loadContacts,
    pickOne,
  };
}
